import { useMemo } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import { SectionShell } from "@/pages/SectionShell";
import { useT } from "@/contexts/I18nContext";
import { useGroups } from "@/lib/hooks/usePeople";
import { RosterTab } from "./RosterTab";
import { TeachersTab } from "./TeachersTab";
import { SettingsTab } from "./SettingsTab";
import { HistoryTab } from "./HistoryTab";
import { ConsentBanner } from "./ConsentBanner";

/**
 * One group, in five tabs.
 *
 * Roster and teachers first, because those are what somebody comes here to
 * change. History last, because it is the only one that is read rather than
 * edited.
 */

type Tab = "roster" | "teachers" | "settings" | "history";

const TABS: readonly { id: Tab; labelKey: string }[] = [
  { id: "roster", labelKey: "group.tab.roster" },
  { id: "teachers", labelKey: "group.tab.teachers" },
  { id: "settings", labelKey: "group.tab.settings" },
  { id: "history", labelKey: "group.tab.history" },
];

const DEFAULT_TAB: Tab = "roster";

function tabFrom(value: string | null): Tab {
  return TABS.some((entry) => entry.id === value) ? (value as Tab) : DEFAULT_TAB;
}

export function GroupPage() {
  const { groupId = "" } = useParams();
  const t = useT();
  const { groups, loading } = useGroups();

  /*
    The tab is in the address, not in a component's memory.

    Held in state, Back from Settings threw the reader out of the group
    entirely — in the live run, into the new-group form (2026-09-09) — and a
    colleague could not be sent a link to the tab being talked about.
  */
  const [params, setParams] = useSearchParams();
  const tab = tabFrom(params.get("tab"));
  const setTab = (next: Tab) => {
    // The roster is the default, and a default does not belong in a URL.
    setParams(next === DEFAULT_TAB ? {} : { tab: next });
  };

  const group = useMemo(
    () => groups.find((candidate) => candidate.id === groupId),
    [groups, groupId]
  );

  if (loading) {
    return (
      <SectionShell titleKey="groups.title">
        <p className="text-sm text-ink-muted">{t("common.loading")}</p>
      </SectionShell>
    );
  }

  if (!group) {
    return (
      <SectionShell titleKey="groups.title">
        <p className="text-ink-muted">{t("error.group.notFound")}</p>
      </SectionShell>
    );
  }

  return (
    <SectionShell titleKey="person.title" titleParams={{ name: group.name }}>
      {/*
        The condition that stops a lesson, said on the group rather than only in
        settings. A teacher should meet it here, not in front of a class.
      */}
      <ConsentBanner group={group} />

      <div role="tablist" className="mt-6 flex flex-wrap gap-1 border-b border-line">
        {TABS.map((entry) => (
          <button
            key={entry.id}
            type="button"
            role="tab"
            aria-selected={entry.id === tab}
            onClick={() => setTab(entry.id)}
            className={`-mb-px border-b-2 px-4 py-2 text-sm transition-colors ${
              entry.id === tab
                ? "border-brand font-medium text-ink"
                : "border-transparent text-ink-muted hover:text-ink"
            }`}
          >
            {t(entry.labelKey)}
          </button>
        ))}
      </div>

      <div className="mt-6">
        {tab === "roster" ? <RosterTab group={group} /> : null}
        {tab === "teachers" ? <TeachersTab group={group} /> : null}
        {tab === "settings" ? <SettingsTab group={group} /> : null}
        {tab === "history" ? <HistoryTab group={group} /> : null}
      </div>
    </SectionShell>
  );
}
